import {useContext, useEffect, useLayoutEffect, useRef, useState} from "react";

import {Card} from "@/components/card/Card.tsx";
import {
  isLivePreviewMessage,
  LIVE_PREVIEW_OVERRIDE,
  LIVE_PREVIEW_READY,
  LivePreviewOverrideMessage,
} from "@/pages/statuspages/shared/livePreview.ts";
import {StatusPagePreviewStateContext} from "@/pages/statuspages/statuspages-manage/ManageStatusPageTabContainer.utils.tsx";

import styles from "./LivePreviewPanel.module.scss";

// The preview always renders the desktop layout. The iframe is laid out at this fixed
// width so the consumption page's media queries resolve to the desktop breakpoint, then
// the whole frame is scaled down to fit the available panel width.
const PREVIEW_VIEWPORT_WIDTH = 1280;

interface LivePreviewPanelProps {
  statusPageId: number;
}

export const LivePreviewPanel = ({statusPageId}: LivePreviewPanelProps) => {
  const {previewReloadKey, previewOverrides} = useContext(StatusPagePreviewStateContext);
  const viewportRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [{width, height}, setSize] = useState({width: PREVIEW_VIEWPORT_WIDTH, height: 0});
  const [frameReady, setFrameReady] = useState(false);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const update = () => setSize({width: viewport.clientWidth, height: viewport.clientHeight});
    update();

    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  // A reload remounts the iframe, so it must re-announce readiness before we send overrides.
  useEffect(() => setFrameReady(false), [previewReloadKey]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      // Only trust readiness from the embedded iframe, not any same-origin window.
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (isLivePreviewMessage(event.data) && event.data.type === LIVE_PREVIEW_READY) {
        setFrameReady(true);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  useEffect(() => {
    if (!frameReady) return;
    const frameWindow = iframeRef.current?.contentWindow;
    if (!frameWindow) return;
    const message: LivePreviewOverrideMessage = {
      type: LIVE_PREVIEW_OVERRIDE,
      overrides: previewOverrides ?? {},
    };
    frameWindow.postMessage(message, window.location.origin);
  }, [frameReady, previewOverrides]);

  const scale = width / PREVIEW_VIEWPORT_WIDTH;
  // Compensate the iframe's CSS height so that, once scaled, it exactly fills the viewport.
  const frameHeight = scale > 0 ? height / scale : 0;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={"pulseTextBodyBold"}>Live Preview</span>
      </div>
      <Card variant="white" className={styles.card}>
        <div className={styles.viewport} ref={viewportRef}>
          <iframe
            key={previewReloadKey}
            ref={iframeRef}
            title={"Status page live preview"}
            src={`/status-page/private/${statusPageId}`}
            className={styles.frame}
            style={{
              width: `${PREVIEW_VIEWPORT_WIDTH}px`,
              height: `${frameHeight}px`,
              transform: `scale(${scale})`,
            }}
          />
        </div>
      </Card>
    </div>
  );
};
